<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-5xl mx-auto px-4 pt-24 pb-6">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-6">
        <button @click="$router.push('/parts-and-services/biddings-manager')" class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-colors mb-4 group">
          <v-icon small class="group-hover:-translate-x-1 transition-transform" color="inherit">mdi-arrow-left</v-icon>
          <span class="text-[10px] font-black uppercase tracking-widest">Voltar para listagem</span>
        </button>
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-black text-slate-800 uppercase tracking-tight">
              {{ id ? 'Editar Licitação' : 'Nova Licitação' }}
            </h1>
            <p class="text-sm text-slate-500 font-medium mt-1">Gestão de licitações, dotações e contratos de peças e serviços</p>
          </div>
          <div v-if="id" class="flex flex-col items-end">
            <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">CÓDIGO DO PROCESSO</span>
            <div class="px-4 py-2 bg-[#333333] rounded-lg shadow-sm border border-slate-700">
              <span class="text-lg font-black text-orange-500 font-mono">#{{ id }}</span>
            </div>
          </div>
        </div>
      </div>

      <v-form ref="form" v-model="valid" lazy-validation>
        <div class="space-y-6">
            
            <v-tabs v-model="tab" grow show-arrows v-show="id ? true : false" class="rounded-t-xl bg-white border-x border-t border-slate-200 overflow-hidden">
              <v-tab :key="0" class="text-xs font-bold tracking-wider uppercase">Dados da Licitação</v-tab>
              <v-tab :key="1" class="text-xs font-bold tracking-wider uppercase">Doação Orçamentária</v-tab>
              <v-tab :key="2" class="text-xs font-bold tracking-wider uppercase">Propostas Ganhadoras</v-tab>
              <v-tab :key="3" class="text-xs font-bold tracking-wider uppercase">Contratos</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="bg-transparent">
              <v-tab-item :key="0">
                <!-- SEÇÃO: DADOS DA LICITAÇÃO -->
                <section class="bg-white rounded-b-xl border border-slate-200 overflow-hidden shadow-sm" :class="{ 'rounded-t-xl': !id }">
                  <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
                    <v-icon color="orange" small>mdi-file-document-outline</v-icon>
                    <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS PRINCIPAIS</h2>
                  </div>
                  
                  <div class="p-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <!-- Prefeitura -->
                      <div class="md:col-span-2">
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Prefeitura</label>
                        <v-autocomplete
                          v-model="data.prefecture_id"
                          :items="prefectures"
                          item-value="prefecture_id"
                          item-text="prefecture_social_name"
                          :rules="fieldRules"
                          placeholder="SELECIONE A PREFEITURA..."
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Numero do Processo -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Número do Processo</label>
                        <v-text-field
                          v-model="data.bidding_process_number"
                          placeholder="000/0000"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Tipo de Licitação -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Tipo de Licitação</label>
                        <v-autocomplete
                          v-model="data.type_id"
                          :items="types"
                          item-value="type_id"
                          item-text="type_desc"
                          :rules="fieldRules"
                          placeholder="SELECIONE O TIPO..."
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Modalidade -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Modalidade</label>
                        <v-autocomplete
                          v-model="data.modality_id"
                          :items="modalities"
                          item-value="modality_id"
                          item-text="modality_desc"
                          :rules="fieldRules"
                          placeholder="SELECIONE A MODALIDADE..."
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Publicação -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Publicação do Edital</label>
                        <v-text-field
                          v-model="data.bidding_publication_notice"
                          v-mask-date.br
                          placeholder="DD/MM/AAAA"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Habilitação -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Habilitação</label>
                        <v-text-field
                          v-model="data.bidding_qualification"
                          v-mask-date.br
                          placeholder="DD/MM/AAAA"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Julgamento -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Julgamento das Propostas</label>
                        <v-text-field
                          v-model="data.bidding_tenders"
                          v-mask-date.br
                          placeholder="DD/MM/AAAA"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Homologação -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Homologação</label>
                        <v-text-field
                          v-model="data.bidding_homologation"
                          v-mask-date.br
                          placeholder="DD/MM/AAAA"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Numero das Propostas -->
                      <div>
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Número das Propostas</label>
                        <v-text-field
                          v-model="data.bidding_number_of_proposals"
                          v-mask-number
                          placeholder="0"
                          solo flat dense hide-details
                          class="custom-select"
                        />
                      </div>

                      <!-- Objeto -->
                      <div class="md:col-span-2">
                        <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Objeto</label>
                        <v-textarea
                          v-model="data.bidding_object"
                          placeholder="DESCREVA O OBJETO DA LICITAÇÃO..."
                          solo flat dense hide-details
                          rows="3"
                          class="custom-select"
                        />
                      </div>

                      <!-- Ativo -->
                      <div class="md:col-span-2">
                        <v-checkbox 
                          v-model="data.bidding_status"
                          label="LICITAÇÃO ATIVA"
                          color="orange"
                          hide-details
                          class="mt-0 pt-0"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              </v-tab-item>

              <v-tab-item :key="1" v-if="id">
                <Donations :bidding_id="id" :prefecture_id="data.prefecture_id" />
              </v-tab-item>
              
              <v-tab-item :key="2" v-if="id">
                <Items :bidding_id="id" />
              </v-tab-item>
              
              <v-tab-item :key="3" v-if="id">
                <Contracts :bidding_id="id" />
              </v-tab-item>
            </v-tabs-items>

            <!-- BOTÕES DE AÇÃO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" v-if="tab === 0">
              <div class="bg-slate-50 px-8 py-5 flex justify-end items-center gap-4">
                <button 
                  @click="$router.push('/parts-and-services/biddings-manager')"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    @click="validate"
                    :disabled="!valid || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                  </button>
                </div>
              </div>
            </section>

          </div>
        </v-form>
      </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar multi-line border="left" :color="snack.color" v-model="snack.show" transition="scroll-y-reverse-transition" class="mb-6">
      <div class="flex items-center gap-3">
        <v-icon color="white">mdi-information-outline</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

  </div>
</template>

<script>
import Donations from "./donnations";
import Items from "./items";
import Contracts from "./contracts";

export default {
  props: ["id"],
  components: {
    Donations,
    Items,
    Contracts,
  },
  data: () => ({
    tab: 0,
    valid: false,
    loader: false,
    prefectures: [],
    modalities: [],
    types: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      modality_id: null,
      prefecture_id: null,
      type_id: null,
      bidding_id: null,
      bidding_process_number: "",
      bidding_publication_notice: "",
      bidding_qualification: "",
      bidding_tenders: "",
      bidding_homologation: "",
      bidding_number_of_proposals: 0,
      bidding_object: "",
      bidding_status: false,
    },
  }),
  created() {
    if (this.$route.query.tab) this.tab = parseInt(this.$route.query.tab, 10);

    this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
    this.$http.get("/biddings/modalities").then((r) => (this.modalities = r.data));
    this.$http.get("/biddings/types").then((r) => (this.types = r.data));
    
    // Garantir que o ID é válido e não é a string "undefined"
    if (this.id && this.id !== "undefined") {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/parts-and-services/biddings/${this.id}`).then((r) => {
        if (r.data && r.data.length > 0) {
          const fetchedData = r.data[0];
          
          // Preservar a estrutura inicial do objeto data para evitar campos undefined no template
          this.data = { ...this.data, ...fetchedData };
          
          // Converter datas apenas se existirem
          this.data.bidding_homologation = this.convertDate(this.data.bidding_homologation);
          this.data.bidding_tenders = this.convertDate(this.data.bidding_tenders);
          this.data.bidding_qualification = this.convertDate(this.data.bidding_qualification);
          this.data.bidding_publication_notice = this.convertDate(this.data.bidding_publication_notice);

          if (this.data.created_at) delete this.data.created_at;
        } else {
          // Caso não encontre a licitação, avisar o usuário
          this.snack.show = true;
          this.snack.message = "Licitação não encontrada!";
          this.snack.color = "warning";
          setTimeout(() => this.$router.push("/parts-and-services/biddings-manager"), 2000);
        }
        this.$store.dispatch("setLoader", false);
      }).catch(e => {
        this.snack.show = true;
        this.snack.message = "Erro ao carregar dados: " + e.message;
        this.snack.color = "error";
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atenção, um ou mais campos precisam ser preenchidos!";
      }
    },
    convertDate(v) {
      if (v) {
        v = v.replace("-", "").replace("-", "");
        let year = v.substring(0, 4);
        let month = v.substring(4, 6);
        let day = v.substring(6, 8);
        return `${day}/${month}/${year}`;
      } else {
        return "";
      }
    },
    createOrUpdate() {
      this.loader = true;
      this.$store.dispatch("setLoader", true);
      
      const method = this.id ? 'put' : 'post';
      const url = this.id ? `parts-and-services/biddings/${this.id}` : "/parts-and-services/biddings";
      
      this.$http[method](url, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          setTimeout(() => {
            this.$router.push("/parts-and-services/biddings-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
  },
};
</script>

<style scoped>
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select ::v-deep textarea {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}

::v-deep .v-tabs-slider-wrapper {
  color: orange !important;
}

::v-deep .v-tab--active {
  color: #1e293b !important;
}
</style>
